import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react'
import api from '../../shared/api/request'

type Props = {
  dashboard: any
  users: any[]
  activities: any[]
  onChanged: () => Promise<void>
}

type Summary = {
  term: any
  timetable: { mine: any[]; confirmed: number; pending: number; memberCount: number }
  plans: any[]
  assignments: any[]
  availability: any[]
  manualFillRequests: any[]
  claims: any[]
  notifications: any[]
  memberSkills: Array<{ id: string; name: string; role: string; skills: string[]; points: number }>
  canManage: boolean
}

const dayNames = ['', '周一', '周二', '周三', '周四', '周五', '周六', '周日']
const stateText: Record<string, string> = {
  draft: '草稿', generated: '待审核', published: '已发布', acknowledged: '已确认', completed: '已完成',
  pending: '待审批', approved: '已通过', rejected: '已驳回', replacement_requested: '待替班',
  pending_confirmation: '待确认', confirmed: '已确认', open: '可接取'
}

type ShiftForm = {
  mode: 'single' | 'fixed'
  date: string
  startDate: string
  endDate: string
  weekdays: string[]
  startTime: string
  endTime: string
  requiredCount: string
  bufferMinutes: string
  role: string
  roleCount: string
  skill: string
  skillCount: string
}

type AvailabilityForm = {
  type: string
  recurrence: 'once' | 'date_range' | 'weekly'
  dateStart: string
  dateEnd: string
  weekdays: string[]
  useTermEnd: boolean
  startTime: string
  endTime: string
  reason: string
}

type DutyConfirmation = {
  kind: 'claim' | 'assignment' | 'manual-fill'
  item: any
  declarationChecked: boolean
  warningChecked: boolean
}

function todayDate() {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function newAvailabilityForm(overrides: Partial<AvailabilityForm> = {}): AvailabilityForm {
  const today = todayDate()
  return {
    type: 'hard',
    recurrence: 'once',
    dateStart: today,
    dateEnd: today,
    weekdays: [],
    useTermEnd: false,
    startTime: '08:00',
    endTime: '18:00',
    reason: '',
    ...overrides
  }
}

function newShiftForm(): ShiftForm {
  const today = todayDate()
  return {
    mode: 'single',
    date: today,
    startDate: today,
    endDate: '',
    weekdays: [],
    startTime: '08:00',
    endTime: '09:40',
    requiredCount: '1',
    bufferMinutes: '20',
    role: '',
    roleCount: '1',
    skill: '',
    skillCount: '1'
  }
}

function Panel({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return <section className="panel"><header className="panel-head"><h2>{title}</h2>{action}</header>{children}</section>
}

function Blank({ text }: { text: string }) {
  return <div className="empty"><span>暂无</span><p>{text}</p></div>
}

function State({ value }: { value: string }) {
  return <span className={`status status-${value}`}>{stateText[value] || value}</span>
}

function formatDateLabel(date: string) {
  if (!date) return `${todayDate()}（系统默认）`
  const value = new Date(`${date}T00:00:00`)
  if (Number.isNaN(value.getTime())) return `${todayDate()}（系统默认）`
  return `${date.slice(5)} 周${'日一二三四五六'[value.getDay()]}`
}

function parseDate(date: string) {
  const value = new Date(`${date}T00:00:00`)
  return Number.isNaN(value.getTime()) ? null : value
}

function dateText(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(date: Date, days: number) {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function calendarDayOfWeek(date: string) {
  const value = parseDate(date)
  if (!value) return 1
  return value.getDay() === 0 ? 7 : value.getDay()
}

function termEndDate(term: any) {
  const firstMonday = parseDate(term?.first_monday || '')
  const totalWeeks = Number(term?.total_weeks || 0)
  return firstMonday && totalWeeks > 0 ? dateText(addDays(firstMonday, totalWeeks * 7 - 1)) : ''
}

function recurrenceLabel(item: any) {
  if (item.recurrence === 'weekly') {
    const weekdays = (Array.isArray(item.weekdays) && item.weekdays.length ? item.weekdays : [calendarDayOfWeek(item.date_start)])
      .map((day: any) => dayNames[Number(day)]).filter(Boolean).join('、')
    return `每周固定 · ${weekdays}`
  }
  if (item.recurrence === 'date_range' || item.recurrence === 'long_term') return '连续多日'
  return '单次时间'
}

function availabilityMatchesDate(request: any, date: string) {
  if (date < request.date_start || date > request.date_end) return false
  if (request.recurrence !== 'weekly') return true
  const weekdays = Array.isArray(request.weekdays) && request.weekdays.length
    ? request.weekdays.map(Number)
    : [calendarDayOfWeek(request.date_start)]
  return weekdays.includes(calendarDayOfWeek(date))
}

function startOfWeekText(date: string) {
  const value = parseDate(date)
  if (!value) return ''
  const offset = value.getDay() === 0 ? -6 : 1 - value.getDay()
  return dateText(addDays(value, offset))
}

function assignmentShiftKey(item: any) {
  return item.shift_id || `${item.date || ''}|${item.start_time || ''}|${item.end_time || ''}`
}

function shiftKey(item: any) {
  return item.id || `${item.date || ''}|${item.start_time || ''}|${item.end_time || ''}`
}

const positionText: Record<string, string> = { admin: '部长', deputy: '副部长', finance: '财务负责人', activity: '活动负责人', member: '普通成员' }

function PlanWeekReview({ plan, weekStart, onWeekChange, onFill, canManage }: { plan: any; weekStart: string; onWeekChange: (value: string) => void; onFill: (shift: any) => void; canManage: boolean }) {
  const shifts: any[] = Array.isArray(plan.shifts) ? plan.shifts.slice().sort((a: any, b: any) => `${a.date || ''} ${a.start_time || ''}`.localeCompare(`${b.date || ''} ${b.start_time || ''}`)) : []
  const assignments: any[] = Array.isArray(plan.assignments) ? plan.assignments : []
  const firstShiftDate = shifts[0]?.date || assignments[0]?.date || ''
  const activeStart = weekStart || startOfWeekText(firstShiftDate)
  const activeDate = parseDate(activeStart)
  if (!activeDate) return <Blank text="生成草稿后，这里会按周展示班次和成员"/>

  const weekDays = Array.from({ length: 7 }, (_, index) => {
    const date = dateText(addDays(activeDate, index))
    return { date, name: dayNames[index + 1] }
  })
  const weekDateSet = new Set(weekDays.map(item => item.date))
  const shiftsByDate = shifts.reduce<Record<string, any[]>>((acc, shift) => {
    if (!weekDateSet.has(shift.date)) return acc
    acc[shift.date] = [...(acc[shift.date] || []), shift]
    return acc
  }, {})
  const assignmentsByShift = assignments.reduce<Record<string, any[]>>((acc, assignment) => {
    const key = assignmentShiftKey(assignment)
    acc[key] = [...(acc[key] || []), assignment]
    return acc
  }, {})
  const visibleShifts = weekDays.flatMap(day => shiftsByDate[day.date] || [])
  const visibleAssignments = visibleShifts.reduce((count, shift) => count + (assignmentsByShift[shiftKey(shift)] || []).length, 0)
  const firstPlanDate = shifts[0]?.date || activeStart
  const lastPlanDate = shifts[shifts.length - 1]?.date || activeStart
  const previousStart = dateText(addDays(activeDate, -7))
  const nextStart = dateText(addDays(activeDate, 7))

  return <div className="plan-week-review">
    <div className="week-review-toolbar">
      <button type="button" className="secondary" onClick={() => onWeekChange(previousStart)}>上一周</button>
      <div><strong>{formatDateLabel(weekDays[0].date)} - {formatDateLabel(weekDays[6].date)}</strong><small>本周 {visibleShifts.length} 个班次，已排 {visibleAssignments} 人次</small></div>
      <button type="button" className="secondary" onClick={() => onWeekChange(nextStart)}>下一周</button>
    </div>
    <div className="week-review-range">计划范围：{firstPlanDate} 至 {lastPlanDate}</div>
    <div className="week-roster-grid">
      {weekDays.map(day => {
        const dayShifts = shiftsByDate[day.date] || []
        return <section className="week-day-column" key={day.date}>
          <header><strong>{day.name}</strong><span>{day.date.slice(5)}</span></header>
          {dayShifts.length ? dayShifts.map(shift => {
            const people = assignmentsByShift[shiftKey(shift)] || []
            const pending = (plan.manualFillRequests || []).filter((item: any) => item.shift_id === shift.id && item.status === 'pending')
            const missing = Math.max(0, Number(shift.required_count || 1) - people.length)
            const openSlots = Math.max(0, Number(shift.required_count || 1) - people.length - pending.length)
            return <article className={`week-shift-card ${missing > 0 ? 'has-missing' : ''}`} key={shiftKey(shift)}>
              <div className="week-shift-time"><strong>{shift.start_time}-{shift.end_time}</strong><span>{shift.location || plan.location || '地点待定'}</span></div>
              {shift.requirements?.length > 0 && <div className="shift-requirements">{shift.requirements.map((item: any) => <span key={item.tag}>{positionText[item.tag] || item.tag} {item.count || 1}人</span>)}</div>}
              <div className="week-shift-people">
                {people.length ? people.map(person => <span key={person.id}>{person.member_name}<State value={person.status}/></span>) : <em>暂未分配</em>}
                {pending.map((request: any) => <span className="pending-person" key={request.id}>{request.user_name}<small>待确认</small></span>)}
              </div>
              {missing > 0 && <div className="missing-actions"><small className="missing-note">缺 {missing} 人{pending.length ? `，${pending.length} 人待确认` : ''}</small>{canManage && openSlots > 0 && <button type="button" className="secondary fill-person-btn" onClick={() => onFill(shift)}>补人</button>}</div>}
            </article>
          }) : <div className="week-empty">无班次</div>}
        </section>
      })}
    </div>
  </div>
}

function PersonalScheduleVisual({ assignments }: { assignments: any[] }) {
  const items = assignments
    .slice()
    .sort((a, b) => `${a.date || ''} ${a.start_time || ''}`.localeCompare(`${b.date || ''} ${b.start_time || ''}`))
    .slice(0, 12)

  return <Panel title="我的排班日程">
    {items.length ? <div className="personal-roster">
      {items.map(item => <article className={`roster-card roster-${item.status}`} key={item.id}>
        <div className="roster-date"><strong>{formatDateLabel(item.date)}</strong><span>{item.start_time || '--:--'}-{item.end_time || '--:--'}</span></div>
        <div className="roster-main"><strong>{item.plan_name || '部门排班'}</strong><small>{item.location || '地点待定'}</small></div>
        <State value={item.status}/>
      </article>)}
    </div> : <Blank text="目前没有个人排班，后续发布后会在这里形成可视化日程"/>}
  </Panel>
}

function messageOf(error: unknown) {
  return typeof error === 'object' && error && 'error' in error ? String((error as any).error) : '操作失败，请稍后重试'
}

async function data<T>(url: string): Promise<T> {
  const result = await api.get(url) as unknown as { data: T }
  return result.data
}

function coversAssignment(request: any, assignment: any) {
  return request.type !== 'soft' && availabilityMatchesDate(request, assignment.date) &&
    (request.start_time || '00:00') < assignment.end_time && (request.end_time || '23:59') > assignment.start_time
}

export default function ScheduleWorkspace({ dashboard, users, activities, onChanged }: Props) {
  const [summary, setSummary] = useState<Summary | null>(null)
  const [view, setView] = useState<'overview' | 'timetable' | 'availability' | 'plans' | 'requests' | 'claims'>('overview')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<any>(null)
  const [timetableFile, setTimetableFile] = useState<File | null>(null)
  const [batchFiles, setBatchFiles] = useState<File[]>([])
  const [targetUserId, setTargetUserId] = useState(dashboard.user.id)
  const [termForm, setTermForm] = useState({ academicYear: '2026-2027', semester: '1', firstMonday: '', totalWeeks: '20' })
  const [calendarEntry, setCalendarEntry] = useState(() => ({ name: '', start: todayDate(), end: '', type: 'school_event', scheduleDayOfWeek: '' }))
  const [availabilityForm, setAvailabilityForm] = useState<AvailabilityForm>(() => newAvailabilityForm())
  const [proofFile, setProofFile] = useState<File | null>(null)
  const [timetableConsent, setTimetableConsent] = useState(false)
  const [proofConsent, setProofConsent] = useState(false)
  const [queryForm, setQueryForm] = useState(() => ({ date: todayDate(), startTime: '08:00', endTime: '09:40', bufferMinutes: '20' }))
  const [queryResult, setQueryResult] = useState<any>(null)
  const [shiftForms, setShiftForms] = useState<ShiftForm[]>([newShiftForm()])
  const [planForm, setPlanForm] = useState({ name: '', activityId: '', location: '', pointsActiveClaim: '10', pointsDelegated: '6' })
  const [selectedPlan, setSelectedPlan] = useState<any>(null)
  const [planWeekStart, setPlanWeekStart] = useState('')
  const [skillEditor, setSkillEditor] = useState({ userId: '', skills: '' })
  const [delegateUsers, setDelegateUsers] = useState<Record<string, string>>({})
  const [delegateNotes, setDelegateNotes] = useState<Record<string, string>>({})
  const [fillDialog, setFillDialog] = useState<{ shift: any } | null>(null)
  const [fillCandidates, setFillCandidates] = useState<any>(null)
  const [fillRequirementTag, setFillRequirementTag] = useState('')
  const [fillSelected, setFillSelected] = useState<string[]>([])
  const [fillNote, setFillNote] = useState('')
  const [replacementContext, setReplacementContext] = useState<{ assignment: any; request?: any } | null>(null)
  const [dutyConfirmation, setDutyConfirmation] = useState<DutyConfirmation | null>(null)

  async function load() {
    try {
      setSummary(await data<Summary>('/schedule/summary'))
    } catch (err) {
      setError(messageOf(err))
    }
  }

  useEffect(() => { void load() }, [])
  useEffect(() => {
    if (summary?.term) setTermForm({ academicYear: summary.term.academic_year, semester: String(summary.term.semester), firstMonday: summary.term.first_monday, totalWeeks: String(summary.term.total_weeks) })
  }, [summary?.term?.id])
  useEffect(() => {
    if (!summary || !('Notification' in window) || Notification.permission !== 'granted') return
    const unread = summary.notifications.find(item => !item.read_at)
    if (unread) new Notification(unread.title, { body: unread.content, tag: unread.id })
  }, [summary?.notifications])

  async function run(action: () => Promise<void>, success: string) {
    setBusy(true); setError(''); setMessage('')
    try { await action(); setMessage(success); await load(); await onChanged() } catch (err) { setError(messageOf(err)) } finally { setBusy(false) }
  }

  async function saveTerm(event: FormEvent) {
    event.preventDefault()
    await run(async () => { await api.post('/schedule/terms', { ...termForm, semester: Number(termForm.semester), totalWeeks: Number(termForm.totalWeeks) }) }, '校历已保存，法定节假日模板可继续手动调整。')
  }

  async function uploadTimetable(event: FormEvent) {
    event.preventDefault()
    if (!timetableFile) return
    if (!timetableConsent) { setError('上传课表前，请先确认已了解课表的处理目的和隐私提示。'); return }
    await run(async () => {
      await api.post('/legal/sensitive-consents', {
        category: 'timetable',
        accepted: true,
        channel: 'schedule_timetable_upload',
        details: { warningAcknowledged: true }
      })
      const result = await api.post(`/schedule/timetables/upload?userId=${encodeURIComponent(summary?.canManage ? targetUserId : dashboard.user.id)}`, await timetableFile.arrayBuffer(), {
        headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'X-File-Name': encodeURIComponent(timetableFile.name) }
      }) as unknown as { data: any }
      setPreview(result.data)
    }, '课表识别完成，请核对后确认。')
  }

  async function uploadBatch() {
    if (!batchFiles.length) return
    if (!timetableConsent) { setError('批量上传课表前，请先确认已取得成员授权并了解隐私提示。'); return }
    await run(async () => {
      await api.post('/legal/sensitive-consents', {
        category: 'timetable',
        accepted: true,
        channel: 'schedule_timetable_batch_upload',
        details: { warningAcknowledged: true, memberAuthorizationConfirmed: true }
      })
      for (const file of batchFiles) {
        await api.post('/schedule/timetables/upload?autoMatch=true', await file.arrayBuffer(), {
          headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'X-File-Name': encodeURIComponent(file.name) }
        })
      }
    }, `已识别 ${batchFiles.length} 份课表，请成员登录确认；异常文件会明确提示。`)
  }

  async function confirmTimetable() {
    if (!preview) return
    await run(async () => { await api.post(`/schedule/timetables/imports/${preview.id}/confirm`) }, '课表已确认并参与排班。')
    setPreview(null)
  }

  async function deleteTimetableImport(item: any) {
    const ok = window.confirm(`确定删除“${item.term_label || '未确认'}”这份未确认课表吗？删除后需要重新上传识别。`)
    if (!ok) return
    await run(async () => { await api.delete(`/schedule/timetables/imports/${item.id}`) }, '未确认课表已删除，可以重新上传。')
    if (preview?.id === item.id) setPreview(null)
  }

  async function addCalendarEntry(event: FormEvent) {
    event.preventDefault()
    const term = summary?.term
    if (!term) return
    await run(async () => { await api.post(`/schedule/terms/${term.id}/calendar-overrides`, calendarEntry); setCalendarEntry({ name: '', start: todayDate(), end: '', type: 'school_event', scheduleDayOfWeek: '' }) }, '人工校历事项已添加。')
  }

  async function fileDataUrl(file: File | null) {
    if (!file) return ''
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(reader.error); reader.readAsDataURL(file)
    })
  }

  async function requestUnavailable(event: FormEvent) {
    event.preventDefault()
    await run(async () => {
      if (proofFile && !proofConsent) throw new Error('上传证明前，请先确认已遮盖无关敏感信息。')
      if (proofFile) await api.post('/legal/sensitive-consents', {
        category: 'availability_proof',
        accepted: true,
        channel: 'schedule_availability_proof',
        details: { warningAcknowledged: true }
      })
      const { useTermEnd: _useTermEnd, ...requestForm } = availabilityForm
      await api.post('/schedule/availability/requests', {
        ...requestForm,
        proofName: proofFile?.name,
        proofBase64: await fileDataUrl(proofFile),
        sourceAssignmentId: replacementContext?.assignment.id,
        purpose: replacementContext ? 'replacement' : 'availability'
      })
      setAvailabilityForm(newAvailabilityForm())
      setProofFile(null)
      setProofConsent(false)
      setReplacementContext(null)
    }, replacementContext ? '换班申请已提交；审批通过后会自动发布到替班池。' : availabilityForm.type === 'soft' ? '已记录“尽量不排”的偏好，系统会优先避开。' : '不能排班申请已提交审批。')
  }

  async function queryAvailable(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError('')
    try {
      const params = new URLSearchParams(queryForm)
      setQueryResult(await data(`/schedule/available-members?${params}`))
    } catch (err) { setError(messageOf(err)) } finally { setBusy(false) }
  }

  function updateShift(index: number, key: keyof ShiftForm, value: string) {
    setShiftForms(items => items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item))
  }

  function toggleShiftWeekday(index: number, weekday: string) {
    setShiftForms(items => items.map((item, itemIndex) => {
      if (itemIndex !== index) return item
      const weekdays = item.weekdays.includes(weekday) ? item.weekdays.filter(value => value !== weekday) : [...item.weekdays, weekday]
      return { ...item, weekdays: weekdays.sort((a, b) => Number(a) - Number(b)) }
    }))
  }

  function toggleAvailabilityWeekday(weekday: string) {
    setAvailabilityForm(current => ({
      ...current,
      weekdays: (current.weekdays.includes(weekday)
        ? current.weekdays.filter(value => value !== weekday)
        : [...current.weekdays, weekday]).sort((a, b) => Number(a) - Number(b))
    }))
  }

  async function createPlan(event: FormEvent) {
    event.preventDefault()
    const activity = activities.find(item => item.id === planForm.activityId)
    await run(async () => {
      await api.post('/schedule/plans', {
        ...planForm,
        activityName: activity?.name,
        pointsActiveClaim: Number(planForm.pointsActiveClaim), pointsDelegated: Number(planForm.pointsDelegated),
        shifts: shiftForms.map(item => ({
          mode: item.mode, date: item.date, startDate: item.startDate, endDate: item.endDate, weekdays: item.weekdays,
          startTime: item.startTime, endTime: item.endTime,
          requiredCount: Number(item.requiredCount), bufferMinutes: Number(item.bufferMinutes),
          requirements: [item.role ? { tag: item.role, count: Number(item.roleCount) } : null, item.skill ? { tag: item.skill, count: Number(item.skillCount) } : null].filter(Boolean)
        }))
      })
      setPlanForm({ name: '', activityId: '', location: '', pointsActiveClaim: '10', pointsDelegated: '6' })
      setShiftForms([newShiftForm()])
    }, '排班计划草稿已创建。')
  }

  async function openPlan(id: string) {
    const plan = await data<any>(`/schedule/plans/${id}`)
    const firstDate = Array.isArray(plan.shifts) ? plan.shifts.slice().sort((a: any, b: any) => String(a.date).localeCompare(String(b.date)))[0]?.date : ''
    setPlanWeekStart(startOfWeekText(firstDate || ''))
    setSelectedPlan(plan)
  }

  async function loadFillCandidates(shift: any, requirementTag = '') {
    if (!selectedPlan) return
    setBusy(true); setError('')
    try {
      const params = requirementTag ? `?requirementTag=${encodeURIComponent(requirementTag)}` : ''
      const result = await data<any>(`/schedule/plans/${selectedPlan.id}/shifts/${shift.id}/candidates${params}`)
      setFillCandidates(result)
      setFillRequirementTag(result.requirementTag || '')
    } catch (err) {
      setError(messageOf(err)); setFillDialog(null)
    } finally {
      setBusy(false)
    }
  }

  async function openFillDialog(shift: any) {
    setFillDialog({ shift }); setFillCandidates(null); setFillSelected([]); setFillNote('')
    await loadFillCandidates(shift)
  }

  function toggleFillMember(userId: string) {
    setFillSelected(current => {
      if (current.includes(userId)) return current.filter(id => id !== userId)
      if (fillCandidates && current.length >= fillCandidates.remaining) return current
      return [...current, userId]
    })
  }

  async function submitManualFill(event: FormEvent) {
    event.preventDefault()
    if (!selectedPlan || !fillDialog || !fillSelected.length) return
    const hasConflict = fillCandidates?.exceptionRequired?.some((item: any) => fillSelected.includes(item.id))
    if (hasConflict && !fillNote.trim()) {
      setError('选择存在冲突的成员时，请填写线下沟通说明。')
      return
    }
    await run(async () => {
      await api.post(`/schedule/plans/${selectedPlan.id}/shifts/${fillDialog.shift.id}/manual-fill`, {
        userIds: fillSelected, requirementTag: fillRequirementTag, communicationNote: fillNote
      })
      await openPlan(selectedPlan.id)
      setFillDialog(null); setFillCandidates(null); setFillSelected([]); setFillNote('')
    }, '补人操作已完成；需要确认的成员已收到邀请。')
  }

  async function respondManualFill(request: any, accepted: boolean) {
    if (accepted && (request.requires_declaration || request.requires_warning_acknowledgement)) {
      setDutyConfirmation({ kind: 'manual-fill', item: request, declarationChecked: false, warningChecked: false })
      return
    }
    await run(async () => { await api.post(`/schedule/manual-fill-requests/${request.id}/respond`, { accepted }) }, accepted ? '已接受补位安排。' : '已拒绝补位邀请。')
  }

  async function claimReplacement(claim: any) {
    const eligibility = claim.eligibility || {}
    if (eligibility.status === 'blocked' || eligibility.status === 'exception_required') {
      setError(eligibility.status === 'exception_required'
        ? `当前存在硬性冲突，不能主动接班；确需安排时请联系负责人例外委派：${eligibility.reasons?.join('；') || ''}`
        : `当前不能接班：${eligibility.reasons?.join('；') || ''}`)
      return
    }
    if (eligibility.requiresDeclaration || eligibility.requiresWarningAcknowledgement) {
      setDutyConfirmation({ kind: 'claim', item: claim, declarationChecked: false, warningChecked: false })
      return
    }
    await run(async () => { await api.post(`/schedule/claims/${claim.id}/claim`) }, '接班成功。')
  }

  async function confirmAssignment(assignment: any) {
    if (assignment.requires_availability_declaration || assignment.requires_warning_acknowledgement) {
      setDutyConfirmation({ kind: 'assignment', item: assignment, declarationChecked: false, warningChecked: false })
      return
    }
    await run(async () => { await api.post(`/schedule/assignments/${assignment.id}/acknowledge`) }, '已确认收到排班。')
  }

  async function submitDutyConfirmation(event: FormEvent) {
    event.preventDefault()
    if (!dutyConfirmation) return
    const { kind, item, declarationChecked, warningChecked } = dutyConfirmation
    const requiresDeclaration = kind === 'claim' ? item.eligibility?.requiresDeclaration : kind === 'assignment' ? item.requires_availability_declaration : item.requires_declaration
    const requiresWarning = kind === 'claim' ? item.eligibility?.requiresWarningAcknowledgement : kind === 'assignment' ? item.requires_warning_acknowledgement : item.requires_warning_acknowledgement
    if (requiresDeclaration && !declarationChecked) return setError('请先勾选无课及到岗声明。')
    if (requiresWarning && !warningChecked) return setError('请先确认已了解时间提醒。')
    const body = { availabilityDeclaration: declarationChecked, warningAcknowledged: warningChecked }
    await run(async () => {
      if (kind === 'claim') await api.post(`/schedule/claims/${item.id}/claim`, body)
      if (kind === 'assignment') await api.post(`/schedule/assignments/${item.id}/acknowledge`, body)
      if (kind === 'manual-fill') await api.post(`/schedule/manual-fill-requests/${item.id}/respond`, { accepted: true, ...body })
      setDutyConfirmation(null)
    }, kind === 'claim' ? '接班成功。' : kind === 'manual-fill' ? '已接受补位安排。' : '已确认收到排班。')
  }

  async function delegateReplacement(claim: any) {
    const userId = delegateUsers[claim.id]
    const candidate = claim.delegateCandidates?.find((item: any) => item.id === userId)
    if (!userId || !candidate) return
    if (candidate.status === 'blocked') return setError(`不能委派该成员：${candidate.reasons.join('；')}`)
    const communicationNote = delegateNotes[claim.id]?.trim() || ''
    if (candidate.status === 'exception_required' && !communicationNote) return setError('该成员存在硬性冲突，请填写线下沟通说明。')
    await run(async () => {
      await api.post(`/schedule/claims/${claim.id}/delegate`, { userId, communicationNote })
      setDelegateUsers(current => ({ ...current, [claim.id]: '' }))
      setDelegateNotes(current => ({ ...current, [claim.id]: '' }))
    }, '替班已委派，等待成员确认。')
  }

  async function saveSkills(event: FormEvent) {
    event.preventDefault()
    if (!skillEditor.userId) return
    await run(async () => { await api.put(`/schedule/members/${skillEditor.userId}/skills`, { skills: skillEditor.skills.split(/[,，]/).map(item => item.trim()).filter(Boolean) }) }, '成员技能标签已保存。')
  }

  async function releaseAssignment(assignment: any) {
    const approved = summary?.availability.find(item => item.user_id === dashboard.user.id && item.status === 'approved' && coversAssignment(item, assignment))
    if (approved) {
      await run(async () => { await api.post(`/schedule/assignments/${assignment.id}/release`, { reason: approved.reason, mode: 'open' }) }, '班次已发布到替班任务池。')
      return
    }
    const pending = summary?.availability.find(item => item.user_id === dashboard.user.id && item.status === 'pending' && item.source_assignment_id === assignment.id)
    setReplacementContext({ assignment, request: pending })
    setAvailabilityForm(newAvailabilityForm({
      type: 'hard', recurrence: 'once', dateStart: assignment.date, dateEnd: assignment.date,
      startTime: assignment.start_time, endTime: assignment.end_time, reason: pending?.reason || ''
    }))
    setView('availability')
    setError('')
    setMessage(pending ? '该班次的换班申请正在等待审批，审批通过后会自动进入替班池。' : '已带入班次时间，请填写原因和证明后提交；审批通过后会自动进入替班池。')
  }

  async function acknowledgeAllAssignments() {
    await run(async () => { await api.post('/schedule/assignments/acknowledge-all') }, '待确认排班已全部确认。')
  }

  async function planAction(id: string, action: 'generate' | 'publish') {
    await run(async () => { await api.post(`/schedule/plans/${id}/${action}`); await openPlan(id) }, action === 'generate' ? '排班草稿已生成，请检查缺员和分配原因。' : '排班已发布并通知成员。')
  }

  async function deletePlan(plan: any) {
    const ok = window.confirm(`确定删除“${plan.name}”吗？草稿和未发布的排班记录会一起删除。`)
    if (!ok) return
    await run(async () => {
      await api.delete(`/schedule/plans/${plan.id}`)
      setSelectedPlan(null)
    }, '排班计划已删除。')
  }

  async function withdrawPlan(plan: any) {
    const reason = window.prompt(`请输入撤回“${plan.name}”的原因，成员会收到该说明：`, '排班安排需要调整')
    if (reason === null) return
    if (!reason.trim()) {
      setError('请填写撤回原因。')
      return
    }
    await run(async () => {
      await api.post(`/schedule/plans/${plan.id}/withdraw`, { reason: reason.trim() })
      await openPlan(plan.id)
    }, '排班计划已撤回，可重新生成、修改后发布或删除。')
  }

  const myAssignments = useMemo(() => summary?.assignments.filter(item => item.user_id === dashboard.user.id) || [], [summary, dashboard.user.id])
  const pendingAcknowledgeAssignments = useMemo(() => myAssignments.filter(item => ['published', 'delegated'].includes(item.status)), [myAssignments])
  const bulkAcknowledgeAssignments = useMemo(() => pendingAcknowledgeAssignments.filter(item => !item.requires_availability_declaration && !item.requires_warning_acknowledgement), [pendingAcknowledgeAssignments])
  const myFillRequests = useMemo(() => summary?.manualFillRequests.filter(item => item.user_id === dashboard.user.id) || [], [summary, dashboard.user.id])

  if (!summary) return <div className="loading">正在加载排班工作台...</div>

  return <div className="page-stack schedule-workspace">
    <div className="page-title"><div><p className="eyebrow">课表驱动协同</p><h1>智能排班</h1><p>上传整学期课表，查询无课成员，生成可解释的排班草稿并完成请假、替班闭环。</p></div><span className="count-pill">{summary.timetable.confirmed}/{summary.timetable.memberCount} 已确认</span></div>
    <nav className="schedule-tabs" aria-label="排班功能"><button className={view === 'overview' ? 'active' : ''} onClick={() => setView('overview')}>总览</button><button className={view === 'timetable' ? 'active' : ''} onClick={() => setView('timetable')}>课表</button><button className={view === 'availability' ? 'active' : ''} onClick={() => setView('availability')}>时间申请</button><button className={view === 'plans' ? 'active' : ''} onClick={() => setView('plans')}>排班计划</button>{summary.canManage && <button className={view === 'requests' ? 'active' : ''} onClick={() => setView('requests')}>审批</button>}<button className={view === 'claims' ? 'active' : ''} onClick={() => setView('claims')}>替班池</button></nav>
    {error && <div className="alert error">{error}</div>}{message && <div className="alert">{message}</div>}
    {view === 'timetable' && <label className="consent-check sensitive-upload-notice"><input type="checkbox" checked={timetableConsent} onChange={event => setTimetableConsent(event.target.checked)} /><span>我已了解课表仅用于无课时段与排班冲突判断；上传前会核对姓名，管理者批量上传时已取得成员授权。<a href="/legal/privacy" target="_blank" rel="noreferrer">查看隐私政策</a></span></label>}
    {view === 'availability' && proofFile && <label className="consent-check sensitive-upload-notice"><input type="checkbox" checked={proofConsent} onChange={event => setProofConsent(event.target.checked)} /><span>我确认已遮盖无关身份证号、银行卡号、家庭住址和诊疗细节，并同意平台仅为本次申请处理证明附件。</span></label>}

    {view === 'overview' && <>
      <div className="schedule-metrics"><div><span>当前学期</span><strong>{summary.term?.label || '未设置'}</strong></div><div><span>我的排班</span><strong>{myAssignments.length}</strong></div><div><span>待审批申请</span><strong>{summary.availability.filter(item => item.status === 'pending').length}</strong></div><div><span>开放替班</span><strong>{summary.claims.length}</strong></div></div>
      <div className="dashboard-grid">
        <Panel title="我的近期排班" action={bulkAcknowledgeAssignments.length ? <button className="secondary compact-action" disabled={busy} onClick={() => void acknowledgeAllAssignments()}>一键确认 {bulkAcknowledgeAssignments.length} 项</button> : undefined}>
          <div className="compact-list">
            {myAssignments.slice(0, 8).map(item => <div className="compact-row" key={item.id}>
              <div><strong>{item.plan_name}</strong><small>{item.date} · {item.start_time}-{item.end_time} · {item.location || '待定'}</small></div>
              <div className="row-actions">
                <State value={item.status}/>
                {['published', 'delegated'].includes(item.status) && <button className="text-btn" disabled={busy} onClick={() => void confirmAssignment(item)}>确认</button>}
                {['published', 'acknowledged'].includes(item.status) && <button className="text-btn" disabled={busy} onClick={() => void releaseAssignment(item)}>申请换班</button>}
              </div>
            </div>)}
            {!myAssignments.length && <Blank text="目前没有个人排班"/>}
          </div>
        </Panel>
        <Panel title="通知与待办" action={'Notification' in window && Notification.permission !== 'granted' ? <button className="text-btn" onClick={() => void Notification.requestPermission()}>开启浏览器提醒</button> : undefined}>
          <div className="compact-list">
            {summary.notifications.slice(0, 8).map(item => <div className="compact-row" key={item.id}>
              <div><strong>{item.title}</strong><small>{item.content}</small></div>
              {!item.read_at && <span className="status status-pending">未读</span>}
            </div>)}
            {!summary.notifications.length && <Blank text="没有新通知"/>}
          </div>
        </Panel>
      </div>
      {myFillRequests.length > 0 && <Panel title="排班补位邀请"><div className="approval-list manual-fill-invitations">{myFillRequests.map(request => <article key={request.id}><div><strong>{request.plan_name || '部门排班'} · {request.date} {request.start_time}-{request.end_time}</strong><p>{request.candidate_reasons?.join('；') || '当前时间可安排'}</p>{request.candidate_warnings?.length > 0 && <small className="warning-text">提醒：{request.candidate_warnings.join('；')}</small>}<small>{request.requires_declaration ? '当前学期没有已确认课表，接受前需要本人确认无课。' : request.communication_note || '负责人已为你安排该班次'}</small></div><div className="approval-actions"><button className="danger" disabled={busy} onClick={() => void respondManualFill(request, false)}>拒绝</button><button className="primary" disabled={busy} onClick={() => void respondManualFill(request, true)}>接受</button></div></article>)}</div></Panel>}
      <PersonalScheduleVisual assignments={myAssignments}/>
    </>}

    {view === 'timetable' && <div className="dashboard-grid">
      <Panel title="上传整学期课表"><form className="stack-form" onSubmit={uploadTimetable}>{summary.canManage && <label>关联成员<select value={targetUserId} onChange={event => setTargetUserId(event.target.value)}>{users.map(user => <option value={user.id} key={user.id}>{user.fullName} · {user.studentId || user.username}</option>)}</select></label>}<label>学习通课表文件<input type="file" accept=".xlsx,.xls" onChange={event => setTimetableFile(event.target.files?.[0] || null)} /></label><p className="helper">系统识别学期、姓名、周次、单双周和连续节次。识别后必须确认才参与排班。</p><button className="primary" disabled={busy || !timetableFile}>识别课表</button></form>{summary.canManage && <div className="batch-upload"><strong>开学批量纳入</strong><p className="helper">可同时选择多份课表，系统按课表姓名匹配成员；同名或未建档成员会停止并提示。</p><input type="file" accept=".xlsx,.xls" multiple onChange={event => setBatchFiles(Array.from(event.target.files || []))}/><button className="secondary" disabled={busy || !batchFiles.length} onClick={() => void uploadBatch()}>批量识别 {batchFiles.length || ''}</button></div>}</Panel>
      <Panel title="学期校历">{summary.canManage ? <><form className="form-grid" onSubmit={saveTerm}><label>学年<input value={termForm.academicYear} onChange={event => setTermForm({ ...termForm, academicYear: event.target.value })} placeholder="2026-2027" /></label><label>学期<select value={termForm.semester} onChange={event => setTermForm({ ...termForm, semester: event.target.value })}><option value="1">第1学期</option><option value="2">第2学期</option><option value="3">第3学期</option></select></label><label>第一周星期一<input type="date" value={termForm.firstMonday} onChange={event => setTermForm({ ...termForm, firstMonday: event.target.value })} /></label><label>总周数<input type="number" min="1" max="30" value={termForm.totalWeeks} onChange={event => setTermForm({ ...termForm, totalWeeks: event.target.value })} /></label><p className="helper span-2">2026-2027第1学期预设：8月31日起，共20周。</p><div className="form-actions span-2"><button className="primary" disabled={busy}>保存校历</button></div></form>{summary.term && <><div className="calendar-list">{(summary.term.holidays || []).map((item: any, index: number) => <div key={item.id || index}><span>{item.start}{item.end !== item.start ? ` 至 ${item.end}` : ''}</span><strong>{item.name}</strong></div>)}</div><form className="form-grid calendar-form" onSubmit={addCalendarEntry}><label>事项名称<input required value={calendarEntry.name} onChange={event => setCalendarEntry({ ...calendarEntry, name: event.target.value })}/></label><label>类型<select value={calendarEntry.type} onChange={event => setCalendarEntry({ ...calendarEntry, type: event.target.value })}><option value="school_event">学校事项</option><option value="holiday">停课/放假</option><option value="workday">补班补课</option><option value="department_closed">部门停排</option></select></label><label>开始日期<input type="date" required value={calendarEntry.start} onChange={event => setCalendarEntry({ ...calendarEntry, start: event.target.value })}/></label><label>结束日期<input type="date" value={calendarEntry.end} onChange={event => setCalendarEntry({ ...calendarEntry, end: event.target.value })}/></label>{calendarEntry.type === 'workday' && <label className="span-2">补星期几课程<select value={calendarEntry.scheduleDayOfWeek} onChange={event => setCalendarEntry({ ...calendarEntry, scheduleDayOfWeek: event.target.value })}><option value="">暂不确定</option>{dayNames.slice(1).map((name, index) => <option value={index + 1} key={name}>{name}</option>)}</select></label>}<div className="form-actions span-2"><button className="secondary" disabled={busy}>添加人工调整</button></div></form></>}</> : <div className="system-facts"><div><span>当前学期</span><strong>{summary.term?.label || '管理员尚未设置'}</strong></div><div><span>第一周</span><strong>{summary.term?.first_monday || '-'}</strong></div></div>}</Panel>
      {preview && <Panel title="识别结果"><div className="import-preview"><div className="schedule-metrics compact"><div><span>姓名</span><strong>{preview.detected_name}</strong></div><div><span>学期</span><strong>{preview.term_label}</strong></div><div><span>置信度</span><strong>{preview.confidence}%</strong></div><div><span>课程记录</span><strong>{preview.courses.length}</strong></div></div>{preview.warnings?.length > 0 && <div className="alert error">{preview.warnings.join('；')}</div>}<div className="table-wrap"><table><thead><tr><th>星期</th><th>节次</th><th>课程</th><th>周次</th></tr></thead><tbody>{preview.courses.slice(0, 30).map((course: any, index: number) => <tr key={index}><td>{dayNames[course.dayOfWeek]}</td><td>{course.periodStart}-{course.periodEnd}</td><td>{course.courseName}</td><td>{course.weeks.join(',')}</td></tr>)}</tbody></table></div><button className="primary" disabled={busy || !preview.term_id || (!preview.name_matched && !summary.canManage)} onClick={() => void confirmTimetable()}>确认课表无误</button></div></Panel>}
      <Panel title="我的课表版本"><div className="compact-list">{summary.timetable.mine.map(item => <div className="compact-row" key={item.id}><div><strong>{item.term_label}</strong><small>识别姓名：{item.detected_name} · 置信度 {item.confidence}%</small></div><div className="row-actions"><State value={item.status}/>{item.status !== 'confirmed' && !item.is_current && <button className="text-btn danger-text" disabled={busy} onClick={() => void deleteTimetableImport(item)}>删除</button>}</div></div>)}{!summary.timetable.mine.length && <Blank text="尚未上传课表"/>}</div></Panel>
    </div>}

    {view === 'availability' && <div className="dashboard-grid">
      <Panel title={replacementContext ? '申请换班' : '提交不可用时间'}>
        {replacementContext && <div className="replacement-context"><strong>{replacementContext.assignment.plan_name}</strong><span>{replacementContext.assignment.date} · {replacementContext.assignment.start_time}-{replacementContext.assignment.end_time}</span><p>{replacementContext.request ? '申请正在等待审批，通过后会自动进入替班池。' : '班次时间已自动带入且不可修改，审批通过后会自动进入替班池。'}</p></div>}
        <form className="form-grid" onSubmit={requestUnavailable}>
          <label>排班处理方式<select disabled={Boolean(replacementContext)} value={availabilityForm.type} onChange={event => setAvailabilityForm({ ...availabilityForm, type: event.target.value })}><option value="hard">我这段时间不能排班</option><option value="soft">尽量不要排我</option></select></label>
          <label>时间类型<select disabled={Boolean(replacementContext)} value={availabilityForm.recurrence} onChange={event => {
            const recurrence = event.target.value as AvailabilityForm['recurrence']
            setAvailabilityForm(current => ({
              ...current,
              recurrence,
              dateEnd: recurrence === 'once' ? current.dateStart : current.dateEnd,
              weekdays: recurrence === 'weekly' ? (current.weekdays.length ? current.weekdays : [String(calendarDayOfWeek(current.dateStart))]) : [],
              useTermEnd: false
            }))
          }}><option value="once">单次时间</option><option value="date_range">连续多日</option><option value="weekly">每周固定</option></select></label>
          <label>{availabilityForm.recurrence === 'once' ? '日期' : '开始日期'}<input disabled={Boolean(replacementContext)} required type="date" value={availabilityForm.dateStart} onChange={event => setAvailabilityForm(current => ({ ...current, dateStart: event.target.value, dateEnd: current.recurrence === 'once' ? event.target.value : current.dateEnd }))}/></label>
          {availabilityForm.recurrence !== 'once' && <label>结束日期<input disabled={Boolean(replacementContext) || availabilityForm.useTermEnd} required type="date" min={availabilityForm.dateStart} value={availabilityForm.dateEnd} onChange={event => setAvailabilityForm({ ...availabilityForm, dateEnd: event.target.value })}/></label>}
          {availabilityForm.recurrence === 'weekly' && <fieldset className="span-2 availability-weekdays"><legend>每周重复日期</legend><div className="weekday-picker">{dayNames.slice(1).map((name, index) => {
            const weekday = String(index + 1)
            return <label key={weekday}><input disabled={Boolean(replacementContext)} type="checkbox" checked={availabilityForm.weekdays.includes(weekday)} onChange={() => toggleAvailabilityWeekday(weekday)}/><span>{name}</span></label>
          })}</div></fieldset>}
          {availabilityForm.recurrence === 'weekly' && <div className="span-2 weekday-picker"><label><input disabled={Boolean(replacementContext) || !termEndDate(summary.term)} type="checkbox" checked={availabilityForm.useTermEnd} onChange={event => setAvailabilityForm({ ...availabilityForm, useTermEnd: event.target.checked, dateEnd: event.target.checked ? termEndDate(summary.term) : availabilityForm.dateEnd })}/><span>持续到本学期结束</span></label></div>}
          <label>开始时间<input disabled={Boolean(replacementContext)} required type="time" value={availabilityForm.startTime} onChange={event => setAvailabilityForm({ ...availabilityForm, startTime: event.target.value })}/></label>
          <label>结束时间<input disabled={Boolean(replacementContext)} required type="time" value={availabilityForm.endTime} onChange={event => setAvailabilityForm({ ...availabilityForm, endTime: event.target.value })}/></label>
          <label className="span-2">原因<textarea required disabled={Boolean(replacementContext?.request)} value={availabilityForm.reason} onChange={event => setAvailabilityForm({ ...availabilityForm, reason: event.target.value })}/></label>
          <label className="span-2">证明附件<input disabled={Boolean(replacementContext?.request)} type="file" accept="image/*,.pdf" onChange={event => setProofFile(event.target.files?.[0] || null)}/></label>
          <div className="form-actions span-2">{replacementContext && <button type="button" className="secondary" onClick={() => { setReplacementContext(null); setAvailabilityForm(newAvailabilityForm()) }}>返回普通申请</button>}{!replacementContext?.request && <button className="primary" disabled={busy}>提交</button>}</div>
        </form>
      </Panel>
      {summary.canManage && <Panel title="查询无课成员"><form className="form-grid" onSubmit={queryAvailable}><label>日期<input type="date" required value={queryForm.date} onChange={event => setQueryForm({ ...queryForm, date: event.target.value })}/></label><label>课程缓冲提醒（分钟）<input type="number" min="0" max="180" value={queryForm.bufferMinutes} onChange={event => setQueryForm({ ...queryForm, bufferMinutes: event.target.value })}/></label><label>开始<input type="time" value={queryForm.startTime} onChange={event => setQueryForm({ ...queryForm, startTime: event.target.value })}/></label><label>结束<input type="time" value={queryForm.endTime} onChange={event => setQueryForm({ ...queryForm, endTime: event.target.value })}/></label><p className="helper span-2">推荐 20 分钟。缓冲范围只做提醒和智能排序，不会禁止排班。</p><div className="form-actions span-2"><button className="primary" disabled={busy}>查询</button></div></form>{queryResult && <div className="availability-results"><h3>可用成员 {queryResult.available.length} 人</h3><div className="member-chips">{queryResult.available.map((item: any) => <span key={item.id} title={item.warnings?.join('；') || ''}>{item.name}{item.warnings?.length ? ' · 有提醒' : ''}</span>)}</div><details><summary>查看需声明或存在冲突的成员</summary>{queryResult.declarationRequired?.map((item: any) => <p key={item.id}><strong>{item.name}</strong>：需要本人确认无课</p>)}{queryResult.exceptionRequired?.map((item: any) => <p key={item.id}><strong>{item.name}</strong>：{item.reasons.join('；')}</p>)}{queryResult.blocked?.map((item: any) => <p key={item.id}><strong>{item.name}</strong>：{item.reasons.join('；')}</p>)}</details></div>}</Panel>}
    </div>}

    {view === 'plans' && <>
      {summary.canManage && <Panel title="创建排班计划">
        <form className="form-grid" onSubmit={createPlan}>
          <label>计划名称<input required value={planForm.name} onChange={event => setPlanForm({ ...planForm, name: event.target.value })}/></label>
          <label>关联活动<select value={planForm.activityId} onChange={event => setPlanForm({ ...planForm, activityId: event.target.value })}><option value="">独立值班</option>{activities.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label className="span-2">地点<input value={planForm.location} onChange={event => setPlanForm({ ...planForm, location: event.target.value })}/></label>
          {shiftForms.map((shift, index) => <div className="shift-editor span-2" key={index}>
            <div className="shift-editor-head">
              <div><strong>班次 {index + 1}</strong><small>{shift.mode === 'fixed' ? '长期固定时段，会按日期范围自动生成多个班次' : '临时单次时段，适合活动或一次性值班'}</small></div>
              {shiftForms.length > 1 && <button type="button" className="text-btn danger-text" onClick={() => setShiftForms(items => items.filter((_, itemIndex) => itemIndex !== index))}>删除班次</button>}
            </div>
            <div className="form-grid compact-form">
              <label>班次类型<select value={shift.mode} onChange={event => updateShift(index, 'mode', event.target.value)}>
                <option value="single">临时单次</option>
                <option value="fixed">长期固定</option>
              </select></label>
              <label>人数<input type="number" min="1" value={shift.requiredCount} onChange={event => updateShift(index, 'requiredCount', event.target.value)}/></label>
              {shift.mode === 'single' ? <label className="span-2">日期<input type="date" required value={shift.date} onChange={event => updateShift(index, 'date', event.target.value)}/></label> : <>
                <label>开始日期<input type="date" required value={shift.startDate} onChange={event => updateShift(index, 'startDate', event.target.value)}/></label>
                <label>结束日期<input type="date" required value={shift.endDate} onChange={event => updateShift(index, 'endDate', event.target.value)}/></label>
                <div className="span-2 weekday-picker" role="group" aria-label="每周固定排班日期">
                  {dayNames.slice(1).map((name, dayIndex) => {
                    const value = String(dayIndex + 1)
                    return <label key={value}><input type="checkbox" checked={shift.weekdays.includes(value)} onChange={() => toggleShiftWeekday(index, value)}/><span>{name}</span></label>
                  })}
                </div>
              </>}
              <label>开始<input type="time" value={shift.startTime} onChange={event => updateShift(index, 'startTime', event.target.value)}/></label>
              <label>结束<input type="time" value={shift.endTime} onChange={event => updateShift(index, 'endTime', event.target.value)}/></label>
              <label className="span-2">课程缓冲提醒（分钟）<input type="number" min="0" max="180" value={shift.bufferMinutes} onChange={event => updateShift(index, 'bufferMinutes', event.target.value)}/><span className="field-help">推荐 20 分钟，只提醒临近上下课，不会禁止安排。</span></label>
              <label>职位要求<select value={shift.role} onChange={event => updateShift(index, 'role', event.target.value)}><option value="">不限职位</option><option value="admin">部长</option><option value="deputy">副部长</option><option value="activity">活动负责人</option><option value="finance">财务负责人</option></select></label>
              <label>职位人数<input type="number" min="1" value={shift.roleCount} onChange={event => updateShift(index, 'roleCount', event.target.value)}/></label>
              <label>技能要求<input value={shift.skill} onChange={event => updateShift(index, 'skill', event.target.value)} placeholder="摄影、设备操作等"/></label>
              <label>技能人数<input type="number" min="1" value={shift.skillCount} onChange={event => updateShift(index, 'skillCount', event.target.value)}/></label>
            </div>
          </div>)}
          <div className="form-actions left span-2"><button type="button" className="secondary" onClick={() => setShiftForms(items => [...items, newShiftForm()])}>添加班次</button></div>
          <div className="form-actions span-2"><button className="primary" disabled={busy}>创建计划</button></div>
        </form>
      </Panel>}
      {summary.canManage && <Panel title="成员技能标签"><form className="form-grid" onSubmit={saveSkills}><label>成员<select required value={skillEditor.userId} onChange={event => { const current = summary.memberSkills.find(item => item.id === event.target.value); setSkillEditor({ userId: event.target.value, skills: current?.skills.join('，') || '' }) }}><option value="">选择成员</option>{summary.memberSkills.map(item => <option value={item.id} key={item.id}>{item.name} · {item.points}积分</option>)}</select></label><label>技能标签<input value={skillEditor.skills} onChange={event => setSkillEditor({ ...skillEditor, skills: event.target.value })} placeholder="摄影，设备操作，主持"/></label><div className="form-actions span-2"><button className="secondary" disabled={busy}>保存标签</button></div></form></Panel>}
      <div className="dashboard-grid">
        <Panel title="计划列表">
          <div className="compact-list">
            {summary.plans.map(plan => <button className="schedule-plan-row" key={plan.id} onClick={() => void openPlan(plan.id)}>
              <span><strong>{plan.name}</strong><small>{plan.activity_name || '独立值班'} · {plan.manager_name}</small></span>
              <State value={plan.status}/>
            </button>)}
            {!summary.plans.length && <Blank text="尚未创建排班计划"/>}
          </div>
        </Panel>
        <Panel title="计划审核">
          {selectedPlan ? <div className="plan-detail">
            <div className="plan-detail-title">
              <div><strong>{selectedPlan.name}</strong><small>{selectedPlan.location || '地点待定'} · {selectedPlan.shifts?.length || 0} 个班次</small></div>
              <State value={selectedPlan.status}/>
            </div>
            {selectedPlan.shifts?.some((shift: any) => {
              const assigned = selectedPlan.assignments.filter((item: any) => item.shift_id === shift.id && !['cancelled', 'released'].includes(item.status)).length
              const pending = selectedPlan.manualFillRequests?.filter((item: any) => item.shift_id === shift.id && item.status === 'pending').length || 0
              return assigned + pending < Number(shift.required_count || 1)
            }) && <div className="alert error">存在缺员班次，可直接在对应班次中补人。</div>}
            <PlanWeekReview plan={selectedPlan} weekStart={planWeekStart} onWeekChange={setPlanWeekStart} onFill={shift => void openFillDialog(shift)} canManage={summary.canManage}/>
            {selectedPlan.assignments.length > 0 && <details className="plan-assignment-details">
              <summary>查看成员明细列表</summary>
              <div className="compact-list">
                {selectedPlan.assignments.map((item: any) => <div className="compact-row" key={item.id}>
                  <div><strong>{item.member_name}</strong><small>{item.date} {item.start_time}-{item.end_time} · {item.reasons?.join('；')}</small></div>
                  <State value={item.status}/>
                </div>)}
              </div>
            </details>}
            {summary.canManage && <div className="form-actions left plan-actions">
              {['draft', 'generated'].includes(selectedPlan.status) && <button className="secondary" disabled={busy} onClick={() => void planAction(selectedPlan.id, 'generate')}>生成草稿</button>}
              {selectedPlan.status === 'generated' && <button className="primary" disabled={busy} onClick={() => void planAction(selectedPlan.id, 'publish')}>确认发布</button>}
              {selectedPlan.status === 'published' && <button className="danger" disabled={busy} onClick={() => void withdrawPlan(selectedPlan)}>撤回发布</button>}
              {['draft', 'generated'].includes(selectedPlan.status) && <button className="danger" disabled={busy} onClick={() => void deletePlan(selectedPlan)}>删除计划</button>}
            </div>}
          </div> : <Blank text="选择一个计划查看详情"/>}
        </Panel>
      </div>
    </>}

    {view === 'requests' && <Panel title="不可用申请审批"><div className="approval-list">{summary.availability.filter(item => item.status === 'pending').map(item => <article key={item.id}><div><strong>{item.user_name}</strong><p>{item.date_start} 至 {item.date_end} · {item.reason}</p><small>{recurrenceLabel(item)}</small></div><div className="approval-actions"><button className="secondary" disabled={busy} onClick={() => void run(async () => { await api.post(`/schedule/availability/requests/${item.id}/review`, { approved: true }) }, '申请已通过。')}>通过</button><button className="danger" disabled={busy} onClick={() => void run(async () => { await api.post(`/schedule/availability/requests/${item.id}/review`, { approved: false }) }, '申请已驳回。')}>驳回</button></div></article>)}{!summary.availability.some(item => item.status === 'pending') && <Blank text="没有待审批申请"/>}</div></Panel>}

    {view === 'claims' && <Panel title="替班任务池"><div className="replacement-grid">{summary.claims.map(claim => {
      const eligibility = claim.eligibility || {}
      const selectedCandidate = claim.delegateCandidates?.find((item: any) => item.id === delegateUsers[claim.id])
      const cannotClaim = ['blocked', 'exception_required'].includes(eligibility.status)
      const claimLabel = eligibility.status === 'declaration_required' ? '声明无课后接取' : eligibility.requiresWarningAcknowledgement ? '确认提醒后接取' : cannotClaim ? '当前不能主动接取' : '接取替班'
      return <article className="replacement-item" key={claim.id}>
        <header><State value="open"/><span>+{claim.points} 分</span></header>
        <h3>{claim.plan_name}</h3><p>{claim.date} · {claim.start_time}-{claim.end_time}</p>
        <small>{claim.location || '地点待定'} · 原排班：{claim.original_user_name}</small>
        {eligibility.status === 'declaration_required' && <div className="claim-guidance warning">当前学期没有已确认课表，需要本人声明该时段无课。</div>}
        {eligibility.status === 'exception_required' && <div className="claim-guidance danger">{eligibility.reasons?.join('；')}</div>}
        {eligibility.status === 'blocked' && <div className="claim-guidance danger">{eligibility.reasons?.join('；')}</div>}
        {eligibility.warnings?.length > 0 && <div className="claim-guidance warning">提醒：{eligibility.warnings.join('；')}</div>}
        <button className="primary" disabled={busy || claim.original_user_id === dashboard.user.id || cannotClaim} onClick={() => void claimReplacement(claim)}>{claimLabel}</button>
        {summary.canManage && <div className="delegate-control">
          <select value={delegateUsers[claim.id] || ''} onChange={event => setDelegateUsers(current => ({ ...current, [claim.id]: event.target.value }))}>
            <option value="">选择委派成员</option>
            {(claim.delegateCandidates || []).map((candidate: any) => <option value={candidate.id} key={candidate.id} disabled={candidate.status === 'blocked'}>{candidate.name} · {candidate.status === 'eligible' ? candidate.warnings?.length ? '可安排，有提醒' : '可安排' : candidate.status === 'declaration_required' ? '需本人无课声明' : candidate.status === 'exception_required' ? '需例外沟通' : '不能安排'}</option>)}
          </select>
          {selectedCandidate && <div className={`delegate-candidate-note ${selectedCandidate.status}`}><strong>{selectedCandidate.name}</strong><span>{selectedCandidate.reasons?.join('；') || '没有硬性冲突'}</span>{selectedCandidate.warnings?.length > 0 && <span>提醒：{selectedCandidate.warnings.join('；')}</span>}</div>}
          {selectedCandidate?.status === 'exception_required' && <textarea required value={delegateNotes[claim.id] || ''} onChange={event => setDelegateNotes(current => ({ ...current, [claim.id]: event.target.value }))} placeholder="填写已与成员线下沟通的情况"/>}
          <button className="secondary" disabled={busy || !selectedCandidate || selectedCandidate.status === 'blocked'} onClick={() => void delegateReplacement(claim)}>发起委派</button>
        </div>}
      </article>
    })}{!summary.claims.length && <Blank text="目前没有开放的替班任务"/>}</div></Panel>}
    {dutyConfirmation && <div className="modal" role="dialog" aria-modal="true" aria-label="确认接班条件"><form className="dialog duty-confirm-dialog" onSubmit={submitDutyConfirmation}>
      <div className="dialog-head"><div><p className="eyebrow">到岗确认</p><h2>{dutyConfirmation.item.plan_name || '部门排班'}</h2></div><button type="button" className="text-btn" onClick={() => setDutyConfirmation(null)}>关闭</button></div>
      <p>{dutyConfirmation.item.date} · {dutyConfirmation.item.start_time}-{dutyConfirmation.item.end_time}</p>
      {(dutyConfirmation.kind === 'claim' ? dutyConfirmation.item.eligibility?.requiresDeclaration : dutyConfirmation.kind === 'assignment' ? dutyConfirmation.item.requires_availability_declaration : dutyConfirmation.item.requires_declaration) && <label className="confirmation-check"><input type="checkbox" checked={dutyConfirmation.declarationChecked} onChange={event => setDutyConfirmation(current => current ? { ...current, declarationChecked: event.target.checked } : current)}/><span>我确认该班次时段无课且可以按时到岗，并愿意承担本次排班责任。</span></label>}
      {(dutyConfirmation.kind === 'claim' ? dutyConfirmation.item.eligibility?.requiresWarningAcknowledgement : dutyConfirmation.kind === 'assignment' ? dutyConfirmation.item.requires_warning_acknowledgement : dutyConfirmation.item.requires_warning_acknowledgement) && <label className="confirmation-check warning"><input type="checkbox" checked={dutyConfirmation.warningChecked} onChange={event => setDutyConfirmation(current => current ? { ...current, warningChecked: event.target.checked } : current)}/><span>我已了解：{(dutyConfirmation.kind === 'claim' ? dutyConfirmation.item.eligibility?.warnings : dutyConfirmation.item.candidate_warnings)?.join('；') || '该班次临近课程或处于尽量避让时段'}</span></label>}
      <p className="helper">确认记录会写入排班审计，负责人可以查看。</p>
      <div className="password-gate-actions"><button type="button" className="secondary" onClick={() => setDutyConfirmation(null)}>取消</button><button className="primary" disabled={busy}>确认并继续</button></div>
    </form></div>}
    {fillDialog && <div className="modal" role="dialog" aria-modal="true" aria-label="为缺员班次补人"><form className="dialog fill-dialog" onSubmit={submitManualFill}>
      <div className="dialog-head"><div><p className="eyebrow">手动补位</p><h2>{fillDialog.shift.date} · {fillDialog.shift.start_time}-{fillDialog.shift.end_time}</h2></div><button type="button" className="text-btn" onClick={() => setFillDialog(null)}>关闭</button></div>
      {fillCandidates ? <>
        <div className="fill-summary"><span>已排 {fillCandidates.assignedCount} 人</span><span>待确认 {fillCandidates.pendingCount} 人</span><strong>还可补 {fillCandidates.remaining} 人</strong></div>
        <label>补位名额<select value={fillRequirementTag} onChange={event => { setFillSelected([]); void loadFillCandidates(fillDialog.shift, event.target.value || '__general__') }}>
          {fillCandidates.requirements.filter((item: any) => item.missing > 0).map((item: any) => <option value={item.tag} key={item.tag}>{item.label} · 还缺 {item.missing} 人</option>)}
          {fillCandidates.generalMissing > 0 && <option value="">普通名额 · 还缺 {fillCandidates.generalMissing} 人</option>}
        </select></label>
        <div className="candidate-groups">
          <section><h3>当前可安排</h3>{fillCandidates.available.map((item: any) => <label className={`candidate-row ${item.warnings?.length ? 'warning' : ''}`} key={item.id}><input type="checkbox" checked={fillSelected.includes(item.id)} onChange={() => toggleFillMember(item.id)}/><span><strong>{item.name} · {item.studentId}</strong><small>{[...item.roles, ...item.skills].join('、') || '普通成员'} · {item.reasons.join('；')}</small>{item.warnings?.length > 0 && <small className="warning-text">提醒：{item.warnings.join('；')}</small>}</span></label>)}{!fillCandidates.available.length && <p className="helper">当前没有完全符合条件的成员。</p>}</section>
          {fillCandidates.declarationRequired?.length > 0 && <details open><summary>未确认课表（成员声明后可安排）</summary><div className="conflict-candidates">{fillCandidates.declarationRequired.map((item: any) => <label className="candidate-row warning" key={item.id}><input type="checkbox" checked={fillSelected.includes(item.id)} onChange={() => toggleFillMember(item.id)}/><span><strong>{item.name} · {item.studentId}</strong><small>成员接受补位前需要确认该时段无课并可以到岗。</small></span></label>)}</div></details>}
          {fillCandidates.exceptionRequired?.length > 0 && <details><summary>课程或硬性不可用（仅可例外邀请）</summary><div className="conflict-candidates">{fillCandidates.exceptionRequired.map((item: any) => <label className="candidate-row conflict" key={item.id}><input type="checkbox" checked={fillSelected.includes(item.id)} onChange={() => toggleFillMember(item.id)}/><span><strong>{item.name} · {item.studentId}</strong><small>{item.reasons.join('；')}</small></span></label>)}</div></details>}
          {fillCandidates.blocked?.length > 0 && <details><summary>不能安排</summary><div className="conflict-candidates">{fillCandidates.blocked.map((item: any) => <div className="candidate-row blocked" key={item.id}><span aria-hidden="true">×</span><span><strong>{item.name} · {item.studentId}</strong><small>{item.reasons.join('；')}</small></span></div>)}</div></details>}
        </div>
        {fillCandidates.exceptionRequired?.some((item: any) => fillSelected.includes(item.id)) && <label>线下沟通说明<textarea required value={fillNote} onChange={event => setFillNote(event.target.value)} placeholder="说明已与成员沟通的情况；成员仍需在系统中确认"/></label>}
        <div className="password-gate-actions"><button type="button" className="secondary" onClick={() => setFillDialog(null)}>取消</button><button className="primary" disabled={busy || !fillSelected.length}>确认补人（{fillSelected.length}）</button></div>
      </> : <div className="loading">正在核对课表和可用时间...</div>}
    </form></div>}
  </div>
}
